import * as Yup from "yup";
import { planSubjectsApi } from "../../services/planSubjects/planSubjectsApi";
import { subjectTypesApi } from "../../services/nomenclatures/subjectTypesApi";
import { subjectControlTypesApi } from "../../services/nomenclatures/subjectControlTypesApi";
import { FormikProvider } from "formik";
import CustomInputText from "../../components/forms/CustomInputText";
import CustomDropdown from "../../components/forms/CustomDropdown";
import AbstractNomenclature from "../nomenclatures/common/AbstractNomenclature";
import { PlanSubjectRequest, PlanWithSubjectsResponse, TeacherResponse } from "../../models/dtos";
import { subjectsApi } from "../../services/nomenclatures/subjectsApi";
import { teachersApi } from "../../services/teachers/teachersApi";
import ItemBrowser from "../../components/forms/ItemBrowser";
import { editDialog as createDialog } from "../nomenclatures/Subjects";
import CustomCheckbox from "../../components/forms/CustomCheckbox";
import { useCallback } from "react";
import { specialitiesApi } from "../../services/nomenclatures/specialitiesApi";

const PlanSubjectsList = ({ plan }: { plan: PlanWithSubjectsResponse }) => {
  const [triggerGetSubjects, { isLoading: isGetSubjectsLoading }] = subjectsApi.useLazyGetSubjectsQuery();
  const { data: subjectTypes } = subjectTypesApi.useGetSubjectTypesQuery();
  const { data: subjectControlTypes } = subjectControlTypesApi.useGetSubjectControlTypesQuery();
  const { data: teachers } = teachersApi.useGetTeachersQuery({});
  const { data: specialities } = specialitiesApi.useGetSpecialitiesQuery();
  const createSubjectMutation = subjectsApi.useUpdateSubjectMutation();

  const validationSchema = Yup.object().shape({
    subjectId: Yup.number().required("Полето е задължително."),
    semester: Yup.number().required("Полето е задължително."),
    lectureHours: Yup.number().required("Полето е задължително."),
    exerciseHours: Yup.number().required("Полето е задължително."),
    labHours: Yup.number().required("Полето е задължително."),
    practiceHours: Yup.number().required("Полето е задължително."),
    lectureHoursWeekly: Yup.number().required("Полето е задължително."),
    exerciseHoursWeekly: Yup.number().required("Полето е задължително."),
    labHoursWeekly: Yup.number().required("Полето е задължително."),
    practiceHoursWeekly: Yup.number().required("Полето е задължително."),
    credits: Yup.number().required("Полето е задължително."),
    subjectTypeId: Yup.number().required("Полето е задължително."),
    teacherId: Yup.number().nullable(),
    subjectControlTypeId: Yup.number().required("Полето е задължително."),
  });

  const editDialog = {
    renderForm: (formik: any) => (
      <FormikProvider value={formik}>
        {formik.values.planSubjectId && <CustomInputText label="ID" id="planSubjectId" disabled />}
        <CustomInputText label="Plan ID" id="planId" disabled />
        <CustomInputText label="Семестър" id="semester" type="number" />
        <ItemBrowser
          id="subjectId"
          label="Дисциплина"
          optionsPromise={triggerGetSubjects}
          loading={isGetSubjectsLoading}
          idField="subjectId"
          labelField="name"
          columns={[
            { field: "subjectId", header: "ID", sortable: true, filter: true },
            { field: "name", header: "Име", sortable: true, filter: true },
            { field: "specialityName", header: "Специалност", sortable: true, filter: true },
          ]}
          buttonLabel="Добави нова дисциплина"
          createDialog={createDialog(specialities?.data)}
          createItemMutation={createSubjectMutation}
          header="Добавяне"
        />
        <CustomInputText label="Часове лекции" id="lectureHours" type="number" />
        <CustomInputText label="Часове упражнения" id="exerciseHours" type="number" />
        <CustomInputText label="Часове лабораторни" id="labHours" type="number" />
        <CustomInputText label="Часове практика" id="practiceHours" type="number" />
        <CustomInputText label="Часове лекции седм." id="lectureHoursWeekly" type="number" />
        <CustomInputText label="Часове упражнения седм." id="exerciseHoursWeekly" type="number" />
        <CustomInputText label="Часове лабораторни седм." id="labHoursWeekly" type="number" />
        <CustomInputText label="Часове практика седм." id="practiceHoursWeekly" type="number" />
        <CustomInputText label="Кредити" id="credits" type="number" />
        <CustomCheckbox id="isCreditExcluded" label="Изключено от кредити" />
        <CustomDropdown
          id="subjectTypeId"
          label="Тип на дисциплината"
          options={subjectTypes?.data || []}
          optionLabel="name"
          optionValue="subjectTypeId"
        />
        {(formik.values.subjectTypeId === 2 || formik.values.subjectTypeId === 5) && (
          <>
            <CustomInputText label="Избираем блок" id="electiveGroupNumber" type="number" />
            <CustomInputText label="Брой за избиране" id="electiveGroupCount" type="number" />
          </>
        )}
        <CustomDropdown<TeacherResponse>
          id="teacherId"
          label="Преподавател"
          filter
          options={teachers?.data || []}
          optionLabel={(teacher: TeacherResponse) =>
            `${teacher.firstName} ${teacher.lastName}, ${teacher.academicPositionShortName ?? ""} ${teacher.academicRankShortName ?? ""} (${teacher.teacherId})`
          }
          optionValue="teacherId"
        />
        <CustomDropdown
          id="subjectControlTypeId"
          label="Форма на контрол"
          options={subjectControlTypes?.data || []}
          optionLabel="name"
          optionValue="subjectControlTypeId"
        />
      </FormikProvider>
    ),
    validationSchema,
    item: {
      planId: plan.planId,
    } as PlanSubjectRequest,
  };

  return (
    <div>
      <h3 className="text-xl font-light mb-4">Дисциплини в учебния план:</h3>

      <AbstractNomenclature
        api={{
          useGetListQuery: { data: { data: plan?.planSubjects || [] } },
          useUpdateItemMutation: planSubjectsApi.useUpdatePlanSubjectMutation,
          useDeleteItemMutation: planSubjectsApi.useDeletePlanSubjectMutation,
        }}
        columns={[
          { field: "semester", header: "Семестър", sortable: true, filter: true },
          {
            field: "subjectName",
            header: "Дисциплина",
            sortable: true,
            filter: true,
          },
          { field: "lectureHours", header: "Лекционни часове", sortable: true, filter: true },
          { field: "exerciseHours", header: "Упражнителни часове", sortable: true, filter: true },
          { field: "labHours", header: "Лабораторни часове", sortable: true, filter: true },
          { field: "practiceHours", header: "Практически часове", sortable: true, filter: true },
          // { field: "lectureHoursWeekly", header: "Практически часове седм.", sortable: true, filter: true },
          // { field: "exerciseHoursWeekly", header: "Практически часове седм.", sortable: true, filter: true },
          // { field: "labHoursWeekly", header: "Практически часове седм.", sortable: true, filter: true },
          // { field: "practiceHoursWeekly", header: "Практически часове седм.", sortable: true, filter: true },
          { field: "credits", header: "Кредити", sortable: true, filter: true },
          { field: "subjectTypeName", header: "Тип на дисциплината", sortable: true, filter: true },
          { field: "electiveGroupNumber", header: "Избираем блок", sortable: true, filter: true },
          { field: "electiveGroupCount", header: "Брой за избиране", sortable: true, filter: true },
          { field: "teacherName", header: "Преподавател", sortable: true, filter: true },
          { field: "subjectControlTypeName", header: "Форма на контрол", sortable: true, filter: true },
        ]}
        idField="planSubjectId"
        exportFilename="planSubjects"
        editDialog={editDialog}
        rowsNumber={120}
      />
    </div>
  );
};

export default PlanSubjectsList;
